import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';

export interface PatientKrollStatus {
  /** Kroll `Pat.Active`. `false` = closed file. `undefined` = the query never asked. */
  active?: boolean;
  /** Kroll `Pat.NHDeceasedDate` (YYYY-MM-DD). `null` = asked, no date. `undefined` = not asked. */
  deceasedDate?: string | null;
}

interface PatientKrollStatusChipsProps {
  patient: PatientKrollStatus;
  /** Rendered inside a `<Typography>`/`<p>`? Keep everything a span. Default true. */
  inline?: boolean;
}

/** DD/MM/YYYY — the format the rest of patient search uses. Never re-parses through Date. */
const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const [y, m, d] = value.split('-');
  return y && m && d ? `${d}/${m}/${y}` : value;
};

/**
 * Kroll's own annotations on a patient row — deceased, and closed file (GP-1402,
 * Kroll query gap audit finding K-22).
 *
 * PHOX used to filter `Pat.Active = 1` out of patient search, so a patient whose
 * file had been closed was simply absent: "this patient isn't in our system" and
 * "this patient's file is closed" looked identical to staff, and the second one
 * is the answer they actually needed. Kroll's own search returns these rows and
 * marks them; PHOX now does the same, and these chips are the marking.
 *
 * Renders NOTHING when Kroll reports a live file, and nothing when the caller
 * never asked for the flags (`undefined`) — an absent chip must never be read as
 * a positive "this patient is fine" on a query that didn't check.
 */
export const PatientKrollStatusChips: React.FC<PatientKrollStatusChipsProps> = ({
  patient,
  inline = true,
}) => {
  const isDeceased = !!patient.deceasedDate;
  // Strictly `false` — `undefined` means the lookup didn't select Pat.Active.
  const isInactive = patient.active === false;

  if (!isDeceased && !isInactive) return null;

  const chipSx = {
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 600,
    '& .MuiChip-label': { px: 0.75 },
  } as const;

  const deceasedLabel = patient.deceasedDate
    ? `Deceased — Kroll records a date of death of ${formatDate(patient.deceasedDate)}`
    : 'Deceased in Kroll';

  return (
    <Box
      component={inline ? 'span' : 'div'}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {isDeceased && (
        <Tooltip title={deceasedLabel} arrow>
          <Chip
            component="span"
            icon={<HeartBrokenIcon sx={{ fontSize: 14 }} />}
            label="Deceased"
            size="small"
            color="error"
            variant="outlined"
            aria-label={`Deceased: ${patient.deceasedDate}`}
            sx={chipSx}
          />
        </Tooltip>
      )}
      {isInactive && (
        <Tooltip title="Inactive — this patient's Kroll file is closed" arrow>
          <Chip
            component="span"
            icon={<BlockIcon sx={{ fontSize: 14 }} />}
            label="Inactive"
            size="small"
            color="warning"
            variant="outlined"
            aria-label="Inactive Kroll file"
            sx={chipSx}
          />
        </Tooltip>
      )}
    </Box>
  );
};
